import React, { forwardRef } from "react";
import { FieldValues } from "react-hook-form";
import styled from "@emotion/styled";

import Input, { InputProps } from "components/ui/input/Input";

export type InputNumberProps<TFieldValues extends FieldValues = FieldValues> = Omit<
  InputProps<TFieldValues>,
  "type"
>;

export type InputNumberCoerced = <TFieldValues extends FieldValues = FieldValues>(
  props: InputNumberProps<TFieldValues> & { ref?: React.ForwardedRef<HTMLInputElement> },
) => React.ReactElement;

/**
 * InputNumber component with arrows/spinners removed
 * @param control - React Hook Form control object for form integration
 * @param name - Field name for React Hook Form
 * @param props - All other standard input props
 */
const InputNumber = forwardRef(function InputNumber<TFieldValues extends FieldValues = FieldValues>(
  { ...props }: InputNumberProps<TFieldValues>,
  externalRef: React.ForwardedRef<HTMLInputElement>,
) {
  return <StyledInput {...(props as InputProps)} type="number" ref={externalRef} />;
}) as InputNumberCoerced; // Needed for generics and useForm type error

const StyledInput = styled(Input)({
  width: "100%",
  minWidth: 0,
  // Remove spinner arrows for Chromium & Webkit browsers (Chrome, Safari, Edge, etc)
  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },

  // Remove spinner arrows for Firefox
  "&[type=number]": {
    appearance: "textfield",
  },
});

export default InputNumber;
